import {useTranslation} from "react-i18next";
import {Route, Routes, useNavigate} from "react-router-dom";
import NotFound from "./pages/error/NotFound.jsx";
import SignIn from "./pages/auth/SignIn.jsx";
import {useContext} from "react";
import SignUp from "./pages/auth/SignUp.jsx";
import AddonAuth from "./pages/auth/AddonAuth.jsx";
import NoProjectScreen from "./pages/NoProjectScreen.jsx";
import UserProjectList from "./pages/userSettings/UserProjectList.jsx";
import {GlobalContext} from "./utils/context/GlobalContextProvider.jsx";
import ProjectSettings from "./pages/project/ProjectSettings.jsx";
import ProjectStep from "./pages/auth/OnBoarding/Steps/ProjectStep.jsx";
import AccountNameStep from "./pages/auth/OnBoarding/Steps/AccountNameStep.jsx";
import UserProfile from "./pages/userSettings/UserProfile.jsx";
import ChangePassword from "./pages/userSettings/ChangePassword.jsx";
import ResetPassword from "./pages/auth/ResetPassword.jsx";
import ProjectUsers from "./pages/project/ProjectUsers.jsx";
import ContinueFromEmail from "./pages/auth/ContinueFromEmail.jsx";
import ThirdPartyServiceIntegrations
    from "./pages/project/ThirdPartyServiceIntegrations/ThirdPartyServiceIntegrations.jsx";
import CreateIntegrationChoosePlatform
    from "./pages/project/ThirdPartyServiceIntegrations/CreateIntegrationChoosePlatform.jsx";
import MainLeadOverview from "./pages/dashboard/LeadDashboard/MainLeadOverview.jsx";
import MainOrderOverview from "./pages/dashboard/OrderDashboard/MainOrderOverview.jsx";
import DailyOverview from "./pages/dashboard/OrderDashboard/DailyOverview.jsx";
import DiscountAndCouponOverview from "./pages/dashboard/OrderDashboard/DiscountAndCouponOverview.jsx";
import OrderMarketingChannelOverview from "./pages/dashboard/OrderDashboard/OrderMarketingChannelOverview.jsx";
import SignUpGoToEmail from "./pages/auth/SignUpGoToEmail.jsx";
import ContinueFromInvitation from "./pages/auth/ContinueFromInvitation.jsx";
import SetPasswordStep from "./pages/auth/OnBoarding/Steps/SetPasswordStep.jsx";
import AddonProjectStep from "./pages/auth/OnBoarding/Steps/AddonProjectStep.jsx";
import ProjectInvitations from "./pages/project/ProjectInvitations.jsx";
import CampaignPerformance from "./pages/dashboard/OrderDashboard/CampaignPerformance.jsx";
import {OtherSystemType, ProjectPermission} from "./api/authority/generated/index";
import OtherSystemSettings from "./pages/project/OtherSystems/OtherSystemSettings.jsx";
import ProjectRoles from "./pages/project/Roles/ProjectRoles.jsx";
import {useHasPermission, useIsAdministrator} from "./components/auth/rolesAndPermissions.js";
import ProjectSubscription from "./pages/project/ProjectSubscription.jsx";
import OrderMarketingProfitabilityOverview
    from "./pages/dashboard/OrderDashboard/OrderMarketingProfitabilityOverview.jsx";
import LeadMarketingChannelOverview from "./pages/dashboard/LeadDashboard/LeadMarketingChannelOverview.jsx";
import {useBrands} from "./utils/brands/branding.js";
import ProjectDomains from "./pages/project/Domains/ProjectDomains.jsx";
import OrderStatusSettings from "./pages/project/ThirdPartyServiceIntegrations/OrderStatusSettings.jsx";
import ServiceStatus from "./pages/admin/ServiceStatus.jsx";
import NewComponents from "./pages/admin/NewComponents.jsx";
import BasketIcon from "./images/remixicons/Finance/shopping-basket-2-line.js";
import AdIcon from "./images/remixicons/Business/advertisement-line.js";
import MoneyIcon from "./images/remixicons/Finance/money-dollar-circle-line.js";
import SettingsIcon from "./images/remixicons/Media/equalizer-line.js";
import BookIcon from "./images/remixicons/Document/book-3-line.js";
import HelpdeskIcon from "./images/remixicons/Finance/coupon-line.js";
import ServerIcon from "./images/remixicons/Device/server-line.js";
import UsersIcon from "./images/remixicons/UserFaces/group-2-line.js";
import DashboardIcon from "./images/remixicons/Device/dashboard-3-line.js";
import Dashboard2Icon from "./images/remixicons/System/dashboard-line.js";
import ChartIcon from "./images/remixicons/Business/bar-chart-box-line.js";
import SvgIcon from "./components/SvgIcon.jsx";
import {isPreview} from "./utils/environment.js";
import PowerBiDashboard from "./pages/dashboard/PowerBiDashboard/PowerBiDashboard.jsx";

export function useProjectRoutes(currentOnly = false) {
    const {t} = useTranslation();

    let {context, getAuthorityConfiguration} = useContext(GlobalContext);

    let brands = useBrands();

    let hasPermission = useHasPermission();
    let isAdministrator = useIsAdministrator();

    let currentProject = null;

    for (let possibleProject of context.currentUserProjects) {
        if (possibleProject.id === context.currentProjectId) {
            currentProject = possibleProject;
        }
    }

    let routes = [];

    let lastProjectName = t('project.project');

    let defaultRouteSelected = false;

    /** @var ProjectData project */
    for (let project of context.currentUserProjects) {

        let isCurrent = false;

        // noinspection JSUnresolvedReference
        if (project.id === context.currentProjectId) {
            // noinspection JSUnresolvedReference
            lastProjectName = project.name;
            isCurrent = true;
            project = {...currentProject, current: true};
        } else if (currentOnly) {
            continue;
        }

        if (project.id === '30704256-1cdc-493f-b5e0-8871a30885c2') {
            routes.push({
                path: defaultRouteSelected ? '/project/' + project.id + '/powerbi' : '/',
                name: 'Power BI',
                icon: <SvgIcon source={ChartIcon} />,
                page: <PowerBiDashboard project={project}/>
            });
            defaultRouteSelected = true;
        }

        if (project.hasErpIntegration) {

            if (hasPermission(ProjectPermission.DailyOverview, project)) {
                routes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/order/daily-metrics' : '/',
                    group: t('route.sales-performance'),
                    name: t('route.daily-metrics'),
                    icon: <SvgIcon source={BasketIcon} />,
                    page: <DailyOverview project={project}/>
                });
                defaultRouteSelected = true;
            }

            if (hasPermission(ProjectPermission.MainOverview, project)) {
                routes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/order/performance-summary' : '/',
                    group: t('route.sales-performance'),
                    name: t('route.performance-summary'),
                    icon: <SvgIcon source={DashboardIcon} />,
                    page: <MainOrderOverview project={project}/>
                });

                routes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/order/marketing-and-sales' : '/',
                    group: t('route.sales-performance'),
                    name: t('route.marketing-and-sales'),
                    icon: <SvgIcon source={Dashboard2Icon} />,
                    page: <NewComponents project={project}/>
                });

                defaultRouteSelected = true;
            }

            if (
                hasPermission(ProjectPermission.DiscountsAndCoupons, project)
                && (project?.subscription?.currentPackage?.allowDiscountCodeEvaluation ?? false)
            ) {
                routes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/order/sales-promotions' : '/',
                    group: t('route.sales-performance'),
                    name: t('route.sales-promotions'),
                    icon: <SvgIcon source={DashboardIcon} />,
                    page: <DiscountAndCouponOverview project={project}/>
                });
                defaultRouteSelected = true;
            }

            if (hasPermission(ProjectPermission.MarketingChannelOverview, project)) {
                routes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/order/campaign-performance' : '/',
                    group: t('route.marketing'),
                    name: t('route.campaign-performance'),
                    icon: <SvgIcon source={AdIcon} />,
                    page: <OrderMarketingChannelOverview project={project}/>
                });
                defaultRouteSelected = true;
            }

            if (hasPermission(ProjectPermission.Profitability, project)) {
                routes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/order/campaign-profitability' : '/',
                    group: t('route.marketing'),
                    name: t('route.campaign-profitability'),
                    icon: <SvgIcon source={AdIcon} />,
                    page: <OrderMarketingProfitabilityOverview project={project}/>
                });
                defaultRouteSelected = true;
            }

            if (hasPermission(ProjectPermission.MarketingChannelOverview, project)) {
                routes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/order/campaign-comparison' : '/',
                    group: t('route.marketing'),
                    name: t('route.campaign-comparison'),
                    icon: <SvgIcon source={AdIcon} />,
                    page: <CampaignPerformance project={project}/>
                });
                defaultRouteSelected = true;
            }
        }

        if (project.hasLeadIntegration) {

            if (hasPermission(ProjectPermission.MainOverview, project)) {
                routes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/lead-gen/performance-summary' : '/',
                    group: t('route.leadgen'),
                    name: t('route.performance-summary'),
                    icon: <SvgIcon source={UsersIcon} />,
                    page: <MainLeadOverview project={project}/>
                });
                defaultRouteSelected = true;
            }

            if (hasPermission(ProjectPermission.MarketingChannelOverview, project)) {
                routes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/lead-gen/campaign-performance' : '/',
                    group: t('route.lead-marketing'),
                    icon: <SvgIcon source={AdIcon} />,
                    name: t('route.campaign-performance'),
                    page: <LeadMarketingChannelOverview project={project}/>
                });
                defaultRouteSelected = true;
            }

            // TODO: fix those graphs...
            /*if (hasPermission(ProjectPermission.MarketingChannelOverview, project)) {
                routes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/lead-gen/lead-performance' : '/',
                    group: t('route.leadgen'),
                    name: t('route.lead-performance'),
                    page: <LeadPerformance project={project}/>
                });
                defaultRouteSelected = true;
            }*/
        }

        if (project.hasErpIntegration && (project?.subscription?.currentPackage?.allowAffiliateEvaluation ?? false)) {
            if (
                hasPermission(ProjectPermission.ProjectAffiliate, project)
            ) {
                routes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/other-system/affiliate' : '/',
                    group: t('route.other-spends'),
                    name: t('route.affiliate-spends'),
                    icon: <SvgIcon source={MoneyIcon} />,
                    page: <OtherSystemSettings project={project} otherSystemType={OtherSystemType.Affiliate}/>
                });
                defaultRouteSelected = true;
            }

            if (hasPermission(ProjectPermission.ProjectMailing, project)) {
                routes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/other-system/mailing' : '/',
                    group: t('route.other-spends'),
                    name: t('route.mailing-spends'),
                    icon: <SvgIcon source={MoneyIcon} />,
                    page: <OtherSystemSettings project={project} otherSystemType={OtherSystemType.Mailing}/>
                });
                defaultRouteSelected = true;
            }

            if (hasPermission(ProjectPermission.ProjectOfflineSpends, project)) {
                routes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/other-system/other' : '/',
                    group: t('route.other-spends'),
                    name: t('route.other-spends'),
                    icon: <SvgIcon source={MoneyIcon} />,
                    page: <OtherSystemSettings project={project} otherSystemType={OtherSystemType.Other}/>
                });
                defaultRouteSelected = true;
            }
        }


        if (!project.hasErpIntegration && !project.hasLeadIntegration) {
            if (hasPermission(ProjectPermission.ProjectIntegrations, project)) {
                routes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/system-setup' : '/',
                    group: t('route.settings'),
                    name: t('route.system-setup'),
                    icon: <SvgIcon source={SettingsIcon} />,
                    page: <CreateIntegrationChoosePlatform project={project} brand={null}/>,
                });
                defaultRouteSelected = true;
            }
        }

        if (hasPermission(ProjectPermission.ProjectProfile, project)) {
            routes.push({
                path: defaultRouteSelected ? '/project/' + project.id + '/settings' : '/',
                group: t('route.settings'),
                name: t('route.project-settings'),
                icon: <SvgIcon source={SettingsIcon} />,
                page: <ProjectSettings project={project}/>
            });
            defaultRouteSelected = true;
        }

        routes.push({
            path: '/settings/profile',
            group: t('route.settings'),
            name: t('user.my-account'),
            icon: <SvgIcon source={SettingsIcon} />,
            page: <UserProfile project={project}/>
        });

        routes.push({
            path: '/projects',
            group: t('route.settings'),
            name: t('route.projects'),
            icon: <SvgIcon source={SettingsIcon} />,
            page: <UserProjectList/>
        });

        if (hasPermission(ProjectPermission.ProjectIntegrations, project)) {
            routes.push({
                path: '/project/' + project.id + '/settings/domains',
                group: t('route.settings'),
                name: t('route.project-domains'),
                icon: <SvgIcon source={SettingsIcon} />,
                page: <ProjectDomains project={project}/>
            });
        }

        if (hasPermission(ProjectPermission.ProjectBilling, project)) {
            routes.push({
                path: '/project/' + project.id + '/settings/plans',
                group: t('route.settings'),
                name: t('route.subscription'),
                icon: <SvgIcon source={SettingsIcon} />,
                page: <ProjectSubscription project={project}/>
            });
        }

        if (hasPermission(ProjectPermission.ProjectIntegrations, project)) {
            if (!defaultRouteSelected) {
                routes.push({
                    path: '/project/' + project.id + '/settings/integrations',
                    group: t('route.settings'),
                    name: null,
                    page: <ThirdPartyServiceIntegrations project={project}/>
                });
                routes.push({
                    path: '/',
                    group: t('route.settings'),
                    name: t('project.3rd-party-integrations'),
                    page: <ThirdPartyServiceIntegrations project={project}/>
                });
                defaultRouteSelected = true;
            } else {
                routes.push({
                    path: '/project/' + project.id + '/settings/integrations',
                    group: t('route.settings'),
                    name: t('project.3rd-party-integrations'),
                    page: <ThirdPartyServiceIntegrations project={project}/>
                });
            }

            routes.push({
                path: '/project/' + project.id + '/settings/integrations/create',
                group: t('route.settings'),
                name: null,
                page: <CreateIntegrationChoosePlatform project={project} brand={null}/>
            });

            for (let brand of Object.values(brands)) {
                routes.push({
                    path: '/project/' + project.id + '/settings/integrations/create/' + brand['slug'],
                    group: t('route.settings'),
                    name: null,
                    page: <CreateIntegrationChoosePlatform project={project} brand={brand}/>
                });
            }

            if (project.hasErpIntegration) {
                routes.push({
                    path: '/project/' + project.id + '/settings/order-statuses',
                    group: t('route.settings'),
                    name: t('project.order-statuses'),
                    page: <OrderStatusSettings project={project}/>
                });
            }
        }

        if (hasPermission(ProjectPermission.ProjectUsers, project)) {
            routes.push({
                path: '/project/' + project.id + '/settings/users',
                group: t('route.settings'),
                name: t('route.project-users'),
                page: <ProjectUsers project={project}/>
            });
        }

        if (project.isOwner || context.currentIdentity.user.isAdministrator) {
            routes.push({
                path: defaultRouteSelected ? '/project/' + project.id + '/roles' : '/',
                group: t('route.settings'),
                name: t('route.project-user-roles'),
                page: <ProjectRoles project={project}/>
            });
            defaultRouteSelected = true;
        }

        if (hasPermission(ProjectPermission.ProjectInvitations, project)) {
            routes.push({
                path: '/project/' + project.id + '/settings/invitations',
                group: t('route.settings'),
                name: t('route.project-invitations'),
                page: <ProjectInvitations project={project}/>
            });
        }
    }

    if (routes.length === 0) {
        routes.push({
            // Base Route
            path: '/',
            name: t('route.dashboard'),
            icon: <SvgIcon source={DashboardIcon} />,
            page: <NoProjectScreen project={currentProject}/>
        });
    }

    return [routes, lastProjectName];
}

export function useAdminRoutes() {
    const {t} = useTranslation();
    const isAdministrator = useIsAdministrator();


    let adminRoutes = [];

    if (isAdministrator === true) {
        adminRoutes = [
            {
                path: '/admin/projects/status',
                name: t('route.service-status'),
                icon: <SvgIcon source={ServerIcon} />,
                page: <ServiceStatus/>
            },/* {
                path: '/admin/projects/list',
                group: t('route.projects'),
                name: t('route.project-list'),
                page: <UnderConstruction/>
            }, {
                path: '/admin/users',
                name: t('route.user-list'),
                icon: <FontAwesomeIcon icon={fas.faUsers}/>,
                page: <UnderConstruction/>
            }, {
                path: '/service-status/workers',
                name: t('route.worker-status'),
                icon: <FontAwesomeIcon icon={fas.faHeartPulse}/>,
                page: <ServiceStatus/>
            }*/
        ];
    }

    return [adminRoutes];
}

export function useHelpAndLegalRoutes() {
    const {t} = useTranslation();

    return [
        [
            {
                href: 'https://datixo.freshdesk.com/support/solutions',
                name: t('route.knowledge-base'),
                icon: <SvgIcon source={BookIcon} />,
            },
            {
                href: 'https://datixo.freshdesk.com/support/tickets/new',
                name: t('route.helpdesk'),
                icon: <SvgIcon source={HelpdeskIcon} />,
            }

            /*{
                path: '/help/faq',
                group: t('route.help'),
                name: t('route.frequently-asked-questions'),
                icon: <FontAwesomeIcon icon={fas.faLifeRing}/>,
                page: <UnderConstruction/>
            },

            {
                path: '/support/tickets',
                group: t('route.help'),
                name: t('route.support-ticket'),
                page: <UnderConstruction/>
            },
            {
                path: '/legal/terms-of-service',
                name: t('route.terms-of-service'),
                icon: <FontAwesomeIcon icon={fas.faFileContract}/>,
                page: <UnderConstruction/>
            },
            {
                path: '/legal/privacy-policy',
                name: t('route.privacy-policy'),
                icon: <FontAwesomeIcon icon={fas.faSection}/>,
                page: <UnderConstruction/>
            },*/
        ]];
}

export function useOnboardingRoutes() {
    const {t} = useTranslation();

    const navigate = useNavigate();

    return [[
        {
            path: '/onboarding/password',
            name: t('user.set-password'),
            page: <SetPasswordStep/>
        },
        {
            path: '/onboarding/info',
            name: t('user.basic-data'),
            page: <AccountNameStep/>
        },
        {
            path: '/onboarding/project',
            name: t('project.create'),
            page: <ProjectStep/>
        },
        {
            path: '/onboarding/addon-project',
            name: t('onboarding.connect-addon'),
            page: <AddonProjectStep/>
        },
    ]];
}

export function useAuthenticationRoutes() {
    const {t} = useTranslation();

    let [onboardingRoutes] = useOnboardingRoutes();

    return [[
        {
            path: '/sign/in', name: t('auth.sign-in'), page: <SignIn/>
        },
        {
            path: '/sign/up', name: t('auth.sign-up'), page: <SignUp/>
        },
        {
            path: '/sign/in/addon', name: t('onboarding.connect-addon'), page: <AddonAuth/>
        },
        {
            path: '/sign/up/continue-with-email', name: t('auth.sign-up'), page: <SignUpGoToEmail/>
        },
        {
            path: '/reset-password', name: t('auth.reset-password'), page: <ResetPassword/>
        },
        {
            path: '/sign/up/email', name: t('auth.confirm-email'), page: <ContinueFromEmail/>
        },
        {
            path: '/invite', name: t('auth.invitation-confirmation'), page: <ContinueFromInvitation/>
        },
        ...onboardingRoutes
    ]];
}

export function useUserRoutes() {
    const {t} = useTranslation();

    return [
        [
            {
                path: '/settings/profile',
                group: t('user.account-settings'),
                name: t('user.profile'),
                icon: <SvgIcon source={SettingsIcon} />,
                page: <UserProfile/>

            },
            {
                path: '/settings/password',
                group: t('user.account-settings'),
                name: t('auth.password'),
                icon: <SvgIcon source={SettingsIcon} />,
                page: <ChangePassword/>
            },
            /*{
                path: '/settings/connected-accounts',
                group: t('user.account-settings'),
                name: t('user.connected-accounts'),
                icon: <FontAwesomeIcon icon={fas.faCircleNodes}/>,
                page: <UserSettingsConstruction/>
            },*/
            {
                path: '/projects',
                group: t('user.projects'),
                name: t('user.project-list'),
                icon: <SvgIcon source={SettingsIcon} />,
                page: <UserProjectList/>
            },
            /*{
                path: '/feedback',
                group: t('user.experience'),
                name: t('user.give-feedback'),
                icon: <FontAwesomeIcon icon={fas.faCommentDots}/>,
                page: <UserSettingsConstruction/>
            },*/
        ]
    ];
}

export function useRouteList() {

    let [projectRoutes] = useProjectRoutes();

    let [adminRoutes] = useAdminRoutes();

    let [helpAndLegalRoutes] = useHelpAndLegalRoutes();

    let [authenticationRoutes] = useAuthenticationRoutes();

    let [userRoutes] = useUserRoutes();

    return [
        projectRoutes
            .concat(adminRoutes)
            .concat(helpAndLegalRoutes)
            .concat(authenticationRoutes)
            .concat(userRoutes)
    ];
}

function Routing() {
    const [routeList] = useRouteList();

    return (<Routes>
        {routeList
            .filter((route) => {
                return route['page'] !== undefined;
            })
            .map((route) => {
                return (<Route path={route['path']} element={route['page']} key={(route['name'] + route['path'])}/>);
            })
        }
        <Route path="*" element={<NotFound/>}/>
    </Routes>);
}

export default Routing;
